<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div 
      class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
      @click="closeModal"
    ></div>

    <!-- Modal Content -->
    <div 
      class="relative min-w-full md:min-w-[500px] w-full max-w-lg h-[450px] md:h-[500px] rounded-[2rem] shadow-2xl overflow-hidden transform transition-all border-4 border-white/10"
    >
      <!-- Background Image (Full Bleed) -->
      <div class="absolute inset-0 z-0">
        <Swiper 
          :modules="swiperModules" 
          :pagination="{ clickable: true }" 
          :navigation="promotions.length > 1"
          :slides-per-view="1"
          class="h-full coupon-swiper"
        >
          <SwiperSlide v-for="(promo, index) in promotions" :key="index">
            <div class="relative h-full w-full">
              <!-- Full Background Image -->
              <div 
                class="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-[3000ms] hover:scale-110"
                :class="!promo.image_url ? 'bg-gradient-to-br from-[#1a1a1a] via-[#0a0a0a] to-black' : ''"
                :style="promo.image_url ? { backgroundImage: `url(${promo.image_url})` } : {}"
              >
              </div>

              <!-- Floating Content (Directly on Image) -->
              <div class="absolute inset-0 z-[10] flex flex-col items-center justify-center p-6 text-center">
                <!-- Tag -->
                <div v-if="promo.label_text" class="bg-[#0B6A1F] text-white text-[11px] font-black uppercase tracking-[0.2em] px-5 py-2 rounded-full mb-6 shadow-2xl border border-white/20">
                  {{ promo.label_text }}
                </div>

                <!-- Offer Title -->
                <h2 class="text-4xl md:text-5xl font-black text-white tracking-tighter leading-none mb-3 uppercase drop-shadow-[0_4px_15px_rgba(0,0,0,1)]">
                  <span v-if="promo.prefix_text" class="mr-2">{{ promo.prefix_text }}</span>
                  <span class="text-green-400">
                    <span v-if="promo.discount_type === 'flat'">{{ promo.discount_value }}$</span>
                    <span v-else>{{ promo.discount_value }}%</span>
                  </span>
                  <span v-if="promo.suffix_text" class="ml-2">{{ promo.suffix_text }}</span>
                  <span v-else class="ml-2">OFF</span>
                </h2>
                
                <!-- Subtitle -->
                <p 
                  v-if="promo.subtitle_text" 
                  class="text-white font-black text-[10px] md:text-xs uppercase tracking-[0.3em] mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,1)] opacity-90"
                >
                  {{ promo.subtitle_text }}
                </p>
                <p 
                  v-else 
                  class="text-white font-black text-[10px] md:text-xs uppercase tracking-[0.3em] mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,1)] opacity-90"
                >
                  FOR YOUR 1ST BOOKING
                </p>

                <!-- Benefits Section -->
                <div v-if="promo.feature_1_text || promo.feature_2_text || promo.feature_3_text" class="flex items-center justify-center gap-2 md:gap-4 mb-6">
                   <!-- Benefit 1 -->
                   <div v-if="promo.feature_1_text" class="bg-white/10 backdrop-blur-md rounded-2xl p-2 md:p-3 border border-white/10 flex flex-col items-center min-w-[80px] md:min-w-[100px]">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5 text-yellow-500 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span class="text-[8px] md:text-[10px] text-white font-bold leading-tight">{{ promo.feature_1_text }}</span>
                   </div>
                   <!-- Benefit 2 -->
                   <div v-if="promo.feature_2_text" class="bg-white/10 backdrop-blur-md rounded-2xl p-2 md:p-3 border border-white/10 flex flex-col items-center min-w-[80px] md:min-w-[100px]">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5 text-yellow-500 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span class="text-[8px] md:text-[10px] text-white font-bold leading-tight">{{ promo.feature_2_text }}</span>
                   </div>
                   <!-- Benefit 3 -->
                   <div v-if="promo.feature_3_text" class="bg-white/10 backdrop-blur-md rounded-2xl p-2 md:p-3 border border-white/10 flex flex-col items-center min-w-[80px] md:min-w-[100px]">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5 text-yellow-500 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.382-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                      <span class="text-[8px] md:text-[10px] text-white font-bold leading-tight">{{ promo.feature_3_text }}</span>
                   </div>
                </div>

                <!-- Copy Section (Floating) -->
                <div class="w-full max-w-sm">
                  <div class="flex flex-col md:flex-row items-center gap-3 bg-black/40 backdrop-blur-md p-2 rounded-2xl border border-white/20 shadow-2xl">
                    <div class="flex-1 text-2xl font-mono font-black text-white tracking-[0.2em] pl-4">
                      {{ promo.code }}
                    </div>
                    <button 
                      @click="copyCode(promo.code)"
                      class="w-full md:w-auto px-8 py-3 bg-[#0B6A1F] hover:bg-green-700 text-white text-sm font-black rounded-xl shadow-2xl transition-all active:scale-90 flex items-center justify-center gap-2 border border-white/20"
                    >
                      <svg v-if="copiedCode === promo.code" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                      <span>{{ copiedCode === promo.code ? 'COPIED!' : 'COPY CODE' }}</span>
                    </button>
                  </div>
                </div>

                <p class="mt-4 text-[10px] md:text-xs font-bold text-white uppercase tracking-widest opacity-80 drop-shadow-md">
                  Only for 1st time users
                </p>

              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      <!-- Close Button (Top right, outside the card but inside the modal) -->
      <button 
        @click="closeModal" 
        class="absolute top-6 right-6 z-[100] w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition-all backdrop-blur-xl border border-white/30 active:scale-90"
      >
        <span class="sr-only">Close</span>
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { useCoupon } from '@/composables/Coupon'

const isOpen = ref(false)
const promotions = ref<any[]>([])
const copiedCode = ref<string | null>(null)
const swiperModules = [Pagination, Navigation]

const { getPromotions } = useCoupon()

const initializeModal = async () => {
    // Determine if modal should open
    // We only open it on Home load and if promotions exist
    const res = await getPromotions()
    if (res.status === 'success' && res.data && res.data.length > 0) {
        promotions.value = res.data
        // Add a slight delay for better UX (so it pops after hero loads)
        setTimeout(() => {
            isOpen.value = true
        }, 1200)
    }
}

onMounted(() => {
    initializeModal()
})

const copyCode = async (code: string) => {
    try {
        await navigator.clipboard.writeText(code)
        copiedCode.value = code
        
        // Slightly longer delay to show the "COPIED" state then close
        setTimeout(() => {
            closeModal()
        }, 800)

        setTimeout(() => {
            if (copiedCode.value === code) {
                copiedCode.value = null
            }
        }, 2000)
    } catch (e) {
        console.error("Failed to copy", e)
    }
}

const closeModal = () => {
    isOpen.value = false
}
</script>

<style scoped>
/* Deep styling to adjust swiper bullets for modal */
:deep(.swiper-pagination-bullet) {
  background: white !important;
  opacity: 0.5;
}
:deep(.swiper-pagination-bullet-active) {
  background: #0B6A1F !important;
  opacity: 1;
}

/* Custom Navigation Arrows to match Close Button style */
:deep(.swiper-button-next), :deep(.swiper-button-prev) {
    color: white !important;
    background-color: rgba(255, 255, 255, 0.2);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
}

:deep(.swiper-button-next:hover), :deep(.swiper-button-prev:hover) {
    background-color: rgba(255, 255, 255, 0.4);
}

:deep(.swiper-button-next:after), :deep(.swiper-button-prev:after) {
    font-size: 18px !important;
    font-weight: 900;
}

:deep(.swiper-button-disabled) {
    opacity: 0;
    cursor: auto;
    pointer-events: none;
}
</style>
